/**
 * API client for Profile-related endpoints
 */

import axios, { AxiosInstance } from 'axios';
import type { ProfileEditor, ProfileViewer, SelectListItem } from '../types/profile';
import type { ApiResponse } from '../types/common';

const API_BASE = '/api';

export class ProfileApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.client.interceptors.request.use((config) => {
      const token = sessionStorage.getItem('ssoAccessToken');
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
  }

  /**
   * Get current user's profile
   */
  async getProfile(): Promise<any> {
    const response = await this.client.get('/Profile');
    return response.data;
  }

  /**
   * Get dashboard data (SLA, Health, Account Team, etc.)
   */
  async getDashboard(): Promise<any> {
    const response = await this.client.get('/Home/Dashboard');
    return response.data;
  }

  /**
   * Get profile editor model with user data
   */
  async getProfileEditor(): Promise<ProfileEditor> {
    // Use the existing Profile API endpoint
    const profile = await this.getProfile();
    // Transform to ProfileEditor format if needed
    return profile as ProfileEditor;
  }

  /**
   * Update user profile
   */
  async updateProfile(profile: Partial<ProfileEditor>): Promise<ApiResponse<void>> {
    const response = await this.client.post<ApiResponse<void>>('/Profile/Update', profile);
    return response.data;
  }

  /**
   * Update user role
   */
  async updateRole(role: string): Promise<ApiResponse<void>> {
    const response = await this.client.post<ApiResponse<void>>('/Profile/Role', { role });
    return response.data;
  }

  /**
   * Get profile viewer data
   */
  async getProfileViewer(): Promise<ProfileViewer> {
    const profile = await this.getProfile();
    return profile as ProfileViewer;
  }

  /**
   * Update user preferences
   */
  async updatePreferences(preferences: any): Promise<ApiResponse<void>> {
    const response = await this.client.post<ApiResponse<void>>('/Profile/Preferences', preferences);
    return response.data;
  }

  /**
   * Change password
   */
  async changePassword(currentPassword: string, newPassword: string, confirmPassword: string): Promise<ApiResponse<void>> {
    const response = await this.client.post<ApiResponse<void>>('/Profile/ChangePassword', {
      currentPassword,
      newPassword,
      confirmPassword,
    });
    return response.data;
  }

  /**
   * Get dropdown options by category
   * Note: These endpoints may need to be implemented in the backend
   */
  async getDropdownOptions(
    category: number,
    currentValue: string
  ): Promise<SelectListItem[]> {
    const response = await this.client.get<SelectListItem[]>(
      `/Profile/dropdown-options`,
      {
        params: { category, currentValue },
      }
    );
    return response.data;
  }

  /**
   * Get phone country codes
   * Note: This endpoint may need to be implemented in the backend
   */
  async getPhoneCountryCodes(byName: number = 0): Promise<SelectListItem[]> {
    const response = await this.client.get<SelectListItem[]>(
      `/Profile/phone-country-codes`,
      {
        params: { byName },
      }
    );
    return response.data;
  }

  /**
   * Get products for software versions
   * Note: This endpoint may need to be implemented in the backend
   */
  async getProducts(ids: string[]): Promise<SelectListItem[]> {
    const response = await this.client.get<SelectListItem[]>(
      `/Profile/products`,
      {
        params: { id: ids.join(',') },
      }
    );
    return response.data;
  }
}

export const profileApiClient = new ProfileApiClient();
